/** Narrow, versioned bridge. Never send auth tokens, cookies, URLs to fetch,
 * or arbitrary native commands through a download request. */
declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(value: string): void };
    __plateNativeNonce?: string;
  }
}
export function isNativeShell() {
  return !!window.ReactNativeWebView && typeof window.__plateNativeNonce === "string";
}

export function requestNative(type: "auth:start" | "download", payload: Record<string, unknown> = {}): Promise<void> {
  const id = crypto.randomUUID();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new Error("The app did not finish the request. Please try again.")), 300_000);
    function finish(error?: Error) {
      clearTimeout(timeout);
      window.removeEventListener("plate-native-result", listener);
      if (error) reject(error); else resolve();
    }
    function listener(event: Event) {
      const result = (event as CustomEvent).detail;
      if (result?.id !== id) return;
      finish(result.error ? new Error(result.error) : undefined);
    }
    window.addEventListener("plate-native-result", listener);
    try {
      if (!isNativeShell()) throw new Error("Native app support is unavailable.");
      window.ReactNativeWebView!.postMessage(JSON.stringify({ type, id, nonce: window.__plateNativeNonce, ...payload }));
    } catch {
      finish(new Error("Could not reach the app. Please reopen it."));
    }
  });
}

export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  if (isNativeShell()) {
    if (blob.size > 25 * 1024 * 1024) throw new Error("This file is larger than the 25 MB mobile sharing limit. Download it in your browser.");
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
      reader.onerror = () => reject(new Error("Could not read the download."));
      reader.readAsDataURL(blob);
    });
    await requestNative("download", { base64, filename, mime: blob.type || "application/octet-stream" });
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Keep the URL alive long enough for browser download navigation to begin.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}